import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '@buildbee/contracts';

/**
 * Subscription plans. The single source of truth for what each plan costs and unlocks; the API
 * enforces it, the web app renders it, and the Stripe prices must match it
 * (`pnpm --filter @buildbee/api stripe:setup`). Mirrors the public pricing page.
 */

export const PAID_PLANS = ['basic', 'premium'] as const;
export type PaidPlanId = (typeof PAID_PLANS)[number];
export type PlanId = 'trial' | PaidPlanId;

/** Capabilities that only some plans include. Everything not listed here is in every plan. */
export const PLAN_FEATURES = ['financials', 'bids', 'integrations', 'api', 'ai_alerts'] as const;
export type PlanFeature = (typeof PLAN_FEATURES)[number];

export const PLAN_FEATURE_LABELS: Record<PlanFeature, string> = {
  financials: 'Budgets, cost tracking and change orders',
  bids: 'Bids and subcontractor invitations',
  integrations: 'Accounting and calendar integrations',
  api: 'Open API and webhooks',
  ai_alerts: 'AI schedule and budget alerts',
};

export type PlanDefinition = {
  id: PaidPlanId;
  name: string;
  tagline: string;
  /** Per company per month, in cents of `currency` (Canadian dollars). */
  priceCents: number;
  /** Stripe (lowercase) currency code. */
  currency: 'cad';
  interval: 'month';
  /** Stripe price lookup key; `stripe:setup` creates the price with it. */
  lookupKey: string;
  /** Projects that may be created per rolling 12 months; null = unlimited. */
  projectsPerYear: number | null;
  features: readonly PlanFeature[];
  /** Marketing bullets, in pricing-page order. */
  highlights: readonly string[];
  badge?: string;
};

export const PLANS: Record<PaidPlanId, PlanDefinition> = {
  basic: {
    id: 'basic',
    name: 'Basic',
    tagline: 'For general contractors running up to 11 projects a year.',
    priceCents: 25_000,
    currency: 'cad',
    interval: 'month',
    lookupKey: 'buildbee_basic_monthly',
    projectsPerYear: 11,
    features: [],
    highlights: [
      'Up to 11 projects a year',
      'Unlimited users, including subcontractors',
      'Schedules, drawings and documents',
      'RFIs, submittals and daily logs',
      'Punch lists and inspections',
      'iOS and Android apps',
      'Email support',
    ],
  },
  premium: {
    id: 'premium',
    name: 'Premium',
    tagline: 'For contractors running more jobs, with job costing and billing in one place.',
    priceCents: 50_000,
    currency: 'cad',
    interval: 'month',
    lookupKey: 'buildbee_premium_monthly',
    projectsPerYear: null,
    features: PLAN_FEATURES,
    highlights: [
      'Everything in Basic',
      'Unlimited projects',
      'Budgets, cost tracking and change orders',
      'Subcontracts, purchase orders and progress invoicing',
      'Online invoice payments in the client portal',
      'Priority support and a live onboarding session',
    ],
    badge: 'Most complete',
  },
};

export const TRIAL_DAYS = 7;
/** Window for the per-year project allowance. */
export const PROJECT_WINDOW_DAYS = 365;

export function isPaidPlan(plan: string | null | undefined): plan is PaidPlanId {
  return plan === 'basic' || plan === 'premium';
}

/** Which plan a Stripe price belongs to (by lookup key), or null for an unknown price. */
export function planForLookupKey(lookupKey: string | null | undefined): PaidPlanId | null {
  return PAID_PLANS.find((p) => PLANS[p].lookupKey === lookupKey) ?? null;
}

/** The WRKRBZ catalog (`_shared/catalog.json`) version these plan prices match. */
export const CATALOG_VERSION = '2026-10-15';

/**
 * Standalone (non-Hive) Stripe prices live in the same Stripe account as Hive's, which owns the
 * catalog lookup keys (`buildbee_basic_monthly`, ...), so standalone prices use `standalone_<catalog key>`.
 */
export const standaloneLookupKey = (catalogLookupKey: string) => `standalone_${catalogLookupKey}`;

/** Which plan a standalone Stripe price belongs to, or null (Hive's prices and unknown prices). */
export function planForStandaloneLookupKey(lookupKey: string | null | undefined): PaidPlanId | null {
  return PAID_PLANS.find((p) => standaloneLookupKey(PLANS[p].lookupKey) === lookupKey) ?? null;
}

const CATALOG_PLAN_CENTS = Object.fromEntries(PAID_PLANS.map((id) => [id, PLANS[id].priceCents])) as Record<PaidPlanId, number>;

/** The standalone Stripe lookup keys whose live amounts BuildBee shows and charges. */
export function standaloneLookupKeys(): string[] {
  return PAID_PLANS.map((id) => standaloneLookupKey(PLANS[id].lookupKey));
}

/**
 * Prices are edited on Hive's Pricing page, which creates new standalone Stripe prices. The API
 * reads their amounts (by lookup key) and applies them here (the web app too, from
 * /api/public/pricing), so PLANS and every screen built on them match what Checkout charges. A key
 * missing from `amounts` uses the catalog price.
 */
export function applyStandalonePrices(amounts: Readonly<Record<string, number>>): void {
  for (const id of PAID_PLANS) (PLANS[id] as { priceCents: number }).priceCents = amounts[standaloneLookupKey(PLANS[id].lookupKey)] ?? CATALOG_PLAN_CENTS[id];
}

/** Stripe subscription statuses that keep the workspace fully usable (past_due = Stripe is retrying). */
const LIVE_STATUSES = new Set(['active', 'trialing', 'past_due']);

export type BillingState = 'trialing' | 'active' | 'past_due' | 'inactive' | 'suspended';

export type Entitlements = {
  plan: PlanId;
  planName: string;
  state: BillingState;
  /** False once the trial or subscription has ended: the workspace becomes read-only (export still works). */
  active: boolean;
  features: PlanFeature[];
  projectsPerYear: number | null;
  trialEndsAt: Date | null;
  trialDaysLeft: number | null;
};

/** Who bills the workspace: BuildBee's own Stripe subscription, or Hive (WRKRBZ control plane). */
export const BILLING_MODES = ['standalone', 'hive'] as const;
export type BillingMode = (typeof BILLING_MODES)[number];

/** Workspace fields the entitlements are computed from (a full workspace row satisfies it). */
export type EntitlementsInput = {
  plan: string;
  trialEndsAt: Date | null;
  subscriptionStatus?: string | null;
  suspendedAt?: Date | null;
  /** 'hive': plan, status, features and limits come from Hive's last entitlements push. */
  billingMode?: string | null;
  /** Hive-pushed features (null = the plan's defaults). */
  hiveFeatures?: readonly string[] | null;
  /** Hive-pushed limits, e.g. `{ projectsPerYear: 11 }` (a missing key = the plan's default). */
  hiveLimits?: Record<string, number | null> | null;
};

/**
 * What a workspace may do right now. A trial gets Premium features for {@link TRIAL_DAYS} days.
 * A paid plan without a subscription status was assigned by hand (seed data, comped accounts)
 * and counts as active. A Hive-managed workspace (`billingMode: 'hive'`) follows Hive's last push;
 * its `subscriptionStatus` holds the Hive status (trialing, active, past_due, inactive, suspended).
 */
export function entitlements(w: EntitlementsInput, now: Date = new Date()): Entitlements {
  // Suspended by a super admin: read-only like a lapsed plan, whatever the subscription says.
  if (w.suspendedAt) return { ...planEntitlements(w, now), state: 'suspended', active: false, features: [] };
  if (w.billingMode === 'hive') return hiveEntitlements(w, now);
  return planEntitlements(w, now);
}

const HIVE_LIVE_STATUSES = new Set(['trialing', 'active', 'past_due']);

/** Hive status -> entitlements. inactive and suspended are read-only, exactly like a lapsed or suspended standalone plan. */
function hiveEntitlements(w: EntitlementsInput, now: Date): Entitlements {
  // Hive only provisions basic/premium; anything else is treated as a lapsed Basic plan.
  const def = PLANS[isPaidPlan(w.plan) ? w.plan : 'basic'];
  const status = w.subscriptionStatus ?? 'inactive';
  const live = isPaidPlan(w.plan) && HIVE_LIVE_STATUSES.has(status);
  const pushed = w.hiveFeatures;
  const features: PlanFeature[] = pushed ? PLAN_FEATURES.filter((f) => pushed.includes(f)) : [...def.features];
  const limits = w.hiveLimits ?? {};
  const projectsPerYear = 'projectsPerYear' in limits ? (limits.projectsPerYear ?? null) : def.projectsPerYear;
  const ends = status === 'trialing' ? w.trialEndsAt : null;
  return {
    plan: def.id,
    planName: def.name,
    state: status === 'suspended' ? 'suspended' : live ? (status as BillingState) : 'inactive',
    active: live,
    features: live ? features : [],
    projectsPerYear,
    trialEndsAt: ends,
    trialDaysLeft: ends ? Math.max(0, Math.ceil((ends.getTime() - now.getTime()) / 86_400_000)) : null,
  };
}

function planEntitlements(w: EntitlementsInput, now: Date): Entitlements {
  if (isPaidPlan(w.plan)) {
    const def = PLANS[w.plan];
    const status = w.subscriptionStatus ?? null;
    const live = status === null || LIVE_STATUSES.has(status);
    return {
      plan: def.id,
      planName: def.name,
      state: !live ? 'inactive' : status === 'past_due' ? 'past_due' : 'active',
      active: live,
      features: live ? [...def.features] : [],
      projectsPerYear: def.projectsPerYear,
      trialEndsAt: null,
      trialDaysLeft: null,
    };
  }
  const ends = w.trialEndsAt;
  const live = !ends || ends.getTime() > now.getTime();
  return {
    plan: 'trial',
    planName: 'Trial',
    state: live ? 'trialing' : 'inactive',
    active: live,
    features: live ? [...PLAN_FEATURES] : [],
    projectsPerYear: null,
    trialEndsAt: ends,
    trialDaysLeft: ends ? Math.max(0, Math.ceil((ends.getTime() - now.getTime()) / 86_400_000)) : null,
  };
}

export function hasFeature(e: Pick<Entitlements, 'features'>, feature: PlanFeature): boolean {
  return e.features.includes(feature);
}

/** Start of the rolling window the project allowance is counted over. */
export function projectWindowStart(now: Date = new Date()): Date {
  return new Date(now.getTime() - PROJECT_WINDOW_DAYS * 86_400_000);
}

/** Null when another project may be created, otherwise the reason it may not. */
export function projectLimitReason(
  e: Pick<Entitlements, 'projectsPerYear' | 'planName'>,
  createdInWindow: number,
  billingMode: string | null = 'standalone',
): string | null {
  if (e.projectsPerYear === null || createdInWindow < e.projectsPerYear) return null;
  const upgrade = billingMode === 'hive' ? 'Upgrade in Hive for unlimited projects.' : 'Upgrade to Premium for unlimited projects.';
  return `The ${e.planName} plan includes ${e.projectsPerYear} projects a year and this workspace has used all of them. ${upgrade}`;
}

/**
 * tRPC procedure paths that need a plan feature. Matched by prefix, so a whole router is covered
 * by one entry; procedures added to these routers later are gated automatically.
 */
export const FEATURE_PATHS: readonly [prefix: string, feature: PlanFeature][] = [
  ['financials.', 'financials'],
  ['projects.bidding.', 'bids'],
  ['integrations.calendar.', 'integrations'],
  ['integrations.storage.', 'integrations'],
  ['integrations.apiKeys.', 'api'],
  ['integrations.webhooks.', 'api'],
];

export function featureForPath(path: string): PlanFeature | null {
  return FEATURE_PATHS.find(([prefix]) => path.startsWith(prefix))?.[1] ?? null;
}

/** Mutations still allowed when the workspace is inactive: billing, members, settings, profile. */
export function allowedWhenInactive(path: string): boolean {
  return path.startsWith('workspace.');
}

/** Whole-dollar plan price for the pricing page and billing screens, e.g. `$250` (CAD, en-CA). */
export function formatPrice(def: Pick<PlanDefinition, 'priceCents'> & { currency?: string }): string {
  return new Intl.NumberFormat(DEFAULT_LOCALE, {
    style: 'currency',
    currency: (def.currency ?? DEFAULT_CURRENCY).toUpperCase(),
    minimumFractionDigits: def.priceCents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(def.priceCents / 100);
}
